import { DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Content } from 'src/app/models/content';
import { ACTIONS, BUNDLE_NAMES, ENVIRONMENTS } from 'src/app/shared/constants/general-constants';
import { SharedModule } from 'src/app/shared/shared.module';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { ENVIRONMENT } from 'src/environments/environment';
import { DrbState, DrbStateModel, LoadDrbByVersion, LoadDrbVersions } from '../../drbs/redux';
import { ContentState, DeleteContent, LoadContent, ReloadContent } from '../redux';
import { EditContentComponent } from './edit-content/edit-content.component';
import { Router, RouterOutlet } from '@angular/router';

@Component({
  selector: 'app-content',
  templateUrl: './content.component.html',
  styleUrls: ['./content.component.css'],
  providers: [DecimalPipe],
  imports: [SharedModule, RouterOutlet],
  standalone: true
})
export class ContentComponent implements OnInit, OnDestroy {
  public version$: Observable<any> = inject(Store).select(DrbState.versions);
  public content$: Observable<Content[] | undefined> = inject(Store).select(ContentState.content);
  private unsubscribe = new Subject<void>();

  public tableData?: Content[];
  public showActions = [ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.CLONE, ACTIONS.DELETE];

  constructor(
    private store: Store,
    private dialogService: NbDialogService,
    private router: Router
  ) {
    if (!this.store.selectSnapshot(ContentState.content)) {
      this.store.dispatch(LoadContent);
    }
    if (![ENVIRONMENTS.TKSTAGE, ENVIRONMENTS.TKPROD].includes(ENVIRONMENT.name as any)) {
      Object.values(BUNDLE_NAMES).forEach((bundle_name) => {
        if (this.store.selectSnapshot(DrbState.versions)[bundle_name].length === 0) {
          this.store.dispatch(new LoadDrbVersions(bundle_name));
        }
      });
    }
  }

  public ngOnInit() {
    this.content$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.tableData = data));
    this.version$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (![ENVIRONMENTS.TKSTAGE, ENVIRONMENTS.TKPROD].includes(ENVIRONMENT.name as any)) {
        Object.values(BUNDLE_NAMES).forEach((bundle_name) => {
          if (data[bundle_name].length > 0) {
            const versions = [...data[bundle_name]].reverse();
            if (!this.store.selectSnapshot(DrbState.getByKey(bundle_name as keyof DrbStateModel))) {
              this.store.dispatch(new LoadDrbByVersion(versions[0], bundle_name));
            }
          }
        });
      }
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public clickedButton($event) {
    if ($event.action === ACTIONS.REFRESH) {
      this.tableData = undefined;
      this.store.dispatch(ReloadContent);
    }
    if ($event.action === ACTIONS.CREATE) {
      this.dialogService.open(EditContentComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          content: new Content()
        }
      });
    }
    if ($event.action === ACTIONS.VIEW) {
      const content: Content = $event.data;
      this.router.navigate([`/content/table/` + content.content_id]);
    }
    if ($event.action === ACTIONS.EDIT) {
      const content: Content = $event.data;
      this.dialogService.open(EditContentComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          content: Content.copy(content) as Content
        }
      });
    }
    if ($event.action === ACTIONS.CLONE) {
      const content: Content = $event.data;
      this.dialogService.open(EditContentComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          content: Content.clone(content)
        }
      });
    }
    if ($event.action === ACTIONS.DELETE) {
      const content: Content = $event.data;
      this.dialogService
        .open(NgxGenericModalComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            title: $localize`Are you sure you want to delete Content`,
            message: content.content_id + ' - ' + content.headline_text
          }
        })
        .onClose.subscribe((data) => {
          if (data === true) {
            const contentToDelete = Content.copy(content);
            contentToDelete.deleted = true;
            this.store.dispatch(new DeleteContent(contentToDelete));
          }
        });
    }
  }
}
